import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaBullseye, FaLightbulb, FaMapMarkerAlt, FaBirthdayCake, FaComments } from "react-icons/fa";
import API_URL from '../../../utils/api';
import "./DynamicProfiles.css";
export default function DynamicProfiles() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams();
  const [isReady, setIsReady] = useState(false);
  const [fetched, setFetched] = useState(null);

  const stateData = location.state || null;

  const userId = useMemo(() => {
    const raw = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    const parsed = raw ? parseInt(raw, 10) : null;
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const resolved = useMemo(() => {
    // Prefer router state (fast path)
    if (stateData?.partner && stateData?.yourProfile) return stateData;
    if (!fetched || !userId) return null;

    const isUser1 = fetched.user1Id === userId;
    const yourProfile = isUser1 ? fetched.dynamicProfile1 : fetched.dynamicProfile2;
    const partner = isUser1 ? fetched.dynamicProfile2 : fetched.dynamicProfile1;

    return {
      partner,
      yourProfile,
      conversationStarter: fetched.conversationStarter,
      similarities: fetched.similarities
    };
  }, [stateData, fetched, userId]);

  useEffect(() => {
    let isMounted = true;
    if (stateData?.partner && stateData?.yourProfile) return;
    if (!chatId) return;
    // If we refreshed / deeplinked, fetch session details
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/funtellime/session/${chatId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (isMounted) setFetched(data);
      } catch (e) {
        if (isMounted) setFetched({ error: true });
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [chatId, stateData]);

  if (!resolved?.partner || !resolved?.yourProfile) {
    // If we can't resolve the match, send user back
    if (fetched?.error) {
      navigate("/funtellime/text-lobby");
    }
    return null;
  }

  const { partner, yourProfile, conversationStarter, similarities } = resolved;
  const handleStartChat = () => {
    setIsReady(true);
    // Navigate to chat interface
    navigate(`/funtellime/chat/${chatId}`, {
      state: {
        chatId,
        partner,
        yourProfile,
        conversationStarter,
        similarities
      }
    });
  };
  return (
    <div className="dynamic-profiles-container">
      <div className="dynamic-profiles-content">
        <h1 className="profiles-title">You've Been Matched!</h1>
        <p className="profiles-subtitle">Here's what you have in common</p>
        
        {/* Similarities Highlight */}
        <div className="similarities-section">
          <h3><FaBullseye /> Your Shared Connections</h3>
          <div className="similarities-grid">
            {similarities && similarities.map((sim, index) => (
              <div key={index} className="similarity-badge">
                {sim.type === 'interests' && `${sim.value.join(', ')}`}
                {sim.type === 'location' && <><FaMapMarkerAlt /> {sim.value}</>}
                {sim.type === 'age' && <><FaBirthdayCake /> {sim.value}</>}
              </div>
            ))}
          </div>
        </div>
        
        {/* Profile Cards */}
        <div className="profiles-grid">
          {/* Your Profile */}
          <div className="profile-card your-profile">
            <div className="profile-label">You</div>
            <div className="profile-avatar">
              <img 
                src={yourProfile.profilePic || 'https://via.placeholder.com/150'} 
                alt={yourProfile.name}
              />
            </div>
            <h2>{yourProfile.name}</h2>
            <p className="profile-age-location">
              {yourProfile.age} • {yourProfile.country}
            </p>
            <div className="profile-highlights">
              {yourProfile.highlights && yourProfile.highlights.map((highlight, index) => (
                <span key={index} className="highlight-tag">{highlight}</span>
              ))}
            </div>
          </div>
          {/* Connection Arrow */}
          <div className="connection-indicator">
            <div className="connection-line"></div>
            <div className="connection-heart"><FaComments /></div>
          </div>
          {/* Partner Profile */}
          <div className="profile-card partner-profile">
            <div className="profile-label">Your Match</div>
            <div className="profile-avatar">
              <img 
                src={partner.profilePic || 'https://via.placeholder.com/150'} 
                alt={partner.name}
              />
            </div>
            <h2>{partner.name}</h2>
            <p className="profile-age-location">
              {partner.age} • {partner.country}
            </p>
            <div className="profile-highlights">
              {partner.highlights && partner.highlights.map((highlight, index) => (
                <span key={index} className="highlight-tag">{highlight}</span>
              ))}
            </div>
          </div>
        </div>
        
        {/* AI Conversation Starter */}
        <div className="prompt-section">
          <h3><FaLightbulb /> Your AI-Generated Conversation Starter</h3>
          <div className="prompt-card">
            <p>{conversationStarter || "What's something you're passionate about that most people don't know?"}</p>
            <p className="prompt-info">Personalized based on your shared interests</p>
          </div>
        </div>
        
        {/* Start Button */}
        <button 
          className={`start-chat-btn ${isReady ? 'loading' : ''}`}
          onClick={handleStartChat}
          disabled={isReady}
        >
          {isReady ? 'Starting Chat...' : 'Start Chatting'}
        </button>
      </div>
    </div>
  );
}
